// HAND-WRITTEN. The names are derived from the generated
// `hostRpcSignatures.ts`; what is decided here — the passthrough denylist — is
// policy, which is why `yarn rpc:refresh` does not own this file.
//
// The surface itself is snapshotted from giraffe-four/libs/gi-js.
// Provenance: VERIFIED AGAINST THE CURRENT BUILD. Not documented, and NOT A
// CONTRACT — an unpromised internal can move in any release with no changelog.
// See CLAUDE.md > Do / do not.
//
// WHY THIS EXISTS: registration is mechanical. SDKDispatcher.ts does
//   for (const [k, v] of Object.entries(giJS)) if (typeof v === 'function') rpc.register(k, v)
// so gi-js's exported FUNCTION set is the RPC registry, exactly. Validated as
// an oracle 15/15 in both directions against every live measurement this
// workspace has taken — see __tests__/hostRpcNames.test.ts, which is the
// control and must keep passing.
//
// Refresh the surface with: yarn rpc:refresh

import { HOST_RPC_SIGNATURES, HOST_RPC_SNAPSHOT } from "./hostRpcSignatures";

/**
 * Every RPC procedure the host registers, by name.
 *
 * DERIVED from `hostRpcSignatures.ts` rather than kept as a second literal
 * list. Two snapshots of one surface can disagree; one cannot.
 */
export const HOST_RPC_NAMES: ReadonlySet<string> = new Set(HOST_RPC_SIGNATURES.keys());

/**
 * The procedures withheld from the generic passthrough, each with the reason.
 *
 * Four kinds. **Credentials**: procedures that hand back somebody else's
 * secrets. **Reach**: procedures that change something beyond the project the
 * session is driving — other projects, templates, the workspace, its shared
 * layer library, the user's own data, who can see them — where a wrong call
 * lands on other people and nothing in this repo can confirm what it did. The reach entries were refused by ACCIDENT until #39,
 * because the walker missed them; they are refused by decision now.
 *
 * **Patch-only**: procedures that write a whole record the host keeps as a diff,
 * where a partial or stale copy silently resets what it leaves out. They are
 * reachable through `giraffe_sdk_patch` and `manifest/patchable.ts`, never raw.
 *
 * **Gated**: procedures withheld while an open issue decides how an agent
 * should reach them. Unlike the other three kinds this is not a settled
 * judgement: each entry names its issue, and the entry goes when the issue does.
 *
 * `fetchCredentials` is declared, in the host's own words, as *"credentials
 * that both the current user and the current app have access to — access tokens
 * for third-party services (e.g. tile servers, AI APIs) that workspace admins
 * have shared with this app."* It takes **no parameters**, so it is the single
 * easiest call in the whole snapshotted surface to make correctly, and
 * `giraffe_sdk_describe` would otherwise hand an agent that sentence as a menu
 * entry.
 *
 * ## Why a denylist here, when `safety/` inverts exactly this pattern
 *
 * `safety/` allowlists SHAPES, and it is right to: the set of ways a value can
 * fail to survive `JSON.stringify` is open, so anything not known-safe must be
 * refused. This is the other kind of question. A credential is a perfectly
 * well-formed array of plain objects with string fields — it passes every
 * check `safety/` makes, because shape is the wrong instrument for it. What is
 * closed here is the surface: a snapshotted set of named procedures, with a gate
 * (`hostRpcNames.test.ts`) that fires when it moves. A denylist over a closed,
 * gated set is enumerable; the vacuous-entry control below is what keeps it so.
 *
 * ## Provenance
 *
 * Giraffe's own AI assistant blocks these **at its tool boundary** and says so
 * in its system prompt — *"even if a skill or the user asks"*, which is Giraffe
 * judging an instruction alone insufficient. Read out of
 * `docs/giraffe-ai-skills-download/Core Behavior.md`, downloaded 2026-09-14:
 * *documented*, for their assistant, and **not a contract for ours**.
 *
 * ⚠️ **Whether the host refuses these to an arbitrary frame is UNVERIFIED.**
 * Nothing in this repo reaches a live host. Do not soften this on the
 * assumption that the host has its own gate — Giraffe put a gate above theirs.
 *
 * ## What this denies, and what it does not
 *
 * It denies **the generic passthrough**: the SDK tools and
 * `giraffe_sdk_describe`, the only tools where an AGENT chooses the procedure
 * name. It does **not** bind `assertHostRpcName`, so a declared tool may still
 * name one — an app fetching a tile-server token and applying it internally is
 * the legitimate use, and `docs/tool-surface.md` § 4 plans a tool over
 * `fetchCredentials` on exactly that basis.
 *
 * That line is drawn where it can be held. The real property is *the raw value
 * does not cross to the agent*, which is a fact about a tool's RESULT, and no
 * check on a name can see it. For the passthrough the two coincide, because the
 * name IS the request. For a declared tool they do not, and review is the gate.
 */
const CREDENTIALS = "credentials";
const REACH = "reach";
const PATCH_ONLY = "patch-only";
const GATED = "gated";

type DenialKind = typeof CREDENTIALS | typeof REACH | typeof PATCH_ONLY | typeof GATED;

/**
 * What a reach entry changes beyond the project, in words the agent can repeat
 * to the user. Verified against the current build at giraffe-four@50cad4903
 * (2026-10-12) unless the comment says otherwise; paths are under `libs/`.
 */
const REACHES: ReadonlyMap<string, string> = new Map([
  // Changes which team can see a set of projects.
  ["shareWithTeam", "changes which team can see a set of projects"],
  // Workspace-wide settings and properties, for every member.
  ["updateWorkspaceDetails", "changes the workspace's settings for every member"],
  // Creates many projects in one call; the host's own doc gates it to approved apps.
  ["bulkCreateProjects", "creates projects in the workspace"],
  // Creates or updates a project per feature — "can create many duplicate
  // projects if used incorrectly", in the host's words. Its second parameter is
  // a function, so it cannot cross the wire anyway; denied so the refusal
  // says why rather than failing on shape.
  ["syncToProjects", "creates or updates many projects in the workspace"],
  // A feature whose id is not the active project is PATCHed through
  // workspaceStateManager: any project held in memory, templates and other
  // workspaces included. `gi-js/src/lib/workspace.ts#updateProject`.
  ["updateProject", "can rewrite any project in the workspace, templates included"],
  // Patches many projects through the same path as updateProject.
  // `gi-js/src/lib/workspace.ts#patchProperties`.
  ["patchProperties", "rewrites the properties of many projects in the workspace"],
  // POST /api/projects/save. `gi-js/src/lib/workspace.ts#createProject`.
  ["createProject", "creates a new project in the workspace"],
  // User.app_meta, which every project the user opens shares.
  // `gi-js/src/lib/exports/misc.ts#setUserData`.
  ["setUserData", "writes the user's own data, which every one of their projects shares"],
  // An optional projectId lands the upload on any project the user can edit.
  // `gi-js/src/lib/exports/project.ts#uploadProjectAttachment`.
  ["uploadProjectAttachment", "can attach a file to any project in the workspace"],
  ["createProjectAttachmentPrompt", "can attach a file to any project in the workspace"],
  // PATCH /api/layers/{id}/ on the org library record every project using the
  // layer reads. `gi-js/src/lib/exports/layers.ts`, `layer-api/src/api.ts`.
  ["updateLayer", "edits a shared library layer, for every project and template using it"],
  // Replaces the shared layer's data in place when one of that name exists.
  // `layer-api/src/utils/create.ts#updateGeoJSONLayerContents`.
  [
    "updateGeoJSONLayerContents",
    "replaces a shared library layer's data, for every project and template using it",
  ],
  // POST /api/layers/: a new org library record, and a duplicate each time it
  // is called. `layer-api/src/utils/create.ts#createLayer`, `#createGeoJSONLayer`.
  ["createLayer", "creates a layer in the workspace's library"],
  ["createGeoJSONLayer", "creates a layer in the workspace's library"],
  // Apps Giraffe hosts for the whole workspace. Each confirms with the user through a
  // helper the walker cannot see, so the snapshot says asksUser: false.
  // `gi-js/src/lib/exports/project.ts#createApp`, `#updateApp`, `#deleteApp`.
  ["createApp", "publishes a new app to the whole workspace"],
  ["updateApp", "changes an app the whole workspace can open"],
  ["deleteApp", "deletes an app for everyone in the workspace"],
]);

/**
 * The snapshot commit whose procedures a human last classified as read, write
 * or denied. A refresh that moves `HOST_RPC_SNAPSHOT.commit` fails the suite
 * until this matches: anything not listed in `readOnlyRpcs.ts` is a write, so
 * an unreviewed name would otherwise reach `giraffe_sdk_write` with nobody
 * having decided it should.
 */
export const HOST_RPC_REVIEWED_AT = "579d960a5";

/** Reach entries whose job a content pack does without creating anything. */
const LAYER_WRITES: ReadonlySet<string> = new Set([
  "updateLayer",
  "updateGeoJSONLayerContents",
  "createLayer",
  "createGeoJSONLayer",
]);

const DENIED: ReadonlyMap<string, DenialKind> = new Map([
  ["fetchCredentials", CREDENTIALS],
  ["fetchAppCredentials", CREDENTIALS],
  // The user's own signed identity token. `host/` obtains it directly for
  // `hello`; the passthrough has no reason to hand it to an agent.
  ["getUserClaimsJwt", CREDENTIALS],
  ...[...REACHES.keys()].map((name) => [name, REACH] as const),
  // Replaces a whole app record; a partial `public` resets the project's
  // analytics and a partial `featureCategories` wipes every usage (#41).
  ["updateProjectApp", PATCH_ONLY],
  // Can wait on confirmation dialogs in the user's window mid-call, and writes
  // a copy of app "1" taken before them (#50). Verified against the current
  // build at giraffe-four@37a2bb94a (2026-09-24): `importAnalytics.tsx:30,116-158`.
  ["addContentPackToProject", GATED],
]);

export const DENIED_HOST_RPC_NAMES: ReadonlySet<string> = new Set(DENIED.keys());

/**
 * The workspace-level procedures, by name.
 *
 * Unlike the rest of the denylist these are NAMED to the agent, in
 * `giraffe_sdk_describe`: an agent that knows the procedure exists and why it
 * is refused will try it or say it needs it, and the refusal is logged. That
 * friction is the evidence for deciding which ones to bring back in a
 * project-scoped form. A credential procedure is never named.
 */
export const WORKSPACE_LEVEL_HOST_RPC_NAMES: ReadonlySet<string> = new Set(REACHES.keys());

/** The denied names `giraffe_sdk_patch` writes instead. Kept equal to `PATCHABLE`'s writes by test. */
export const PATCH_ONLY_HOST_RPC_NAMES: ReadonlySet<string> = new Set(
  [...DENIED].filter(([, kind]) => kind === PATCH_ONLY).map(([name]) => name),
);

/** Which kind of denial a name is, for the refusal log. `null` for a name that is not denied. */
export function denialKind(name: string): DenialKind | null {
  return DENIED.get(name) ?? null;
}

/** One line per workspace-level procedure: what it changes beyond the project. */
export function workspaceLevelReach(name: string): string | null {
  return REACHES.get(name) ?? null;
}

/**
 * Why a denied name is refused, in the terms its reader can act on.
 *
 * The reader is the agent, and its next move is to **carry on without the
 * value**, which is why the message says the app already applies its own
 * credentials. A message naming no action would read as an obstacle to route
 * around — `manifest/errors.ts` on who a message is written for.
 */
export function deniedRpcReason(name: string): string {
  if (DENIED.get(name) === GATED) {
    return (
      `"${name}" is withheld for now: installing a content pack can stop mid-call to ask the ` +
      `user a question in their Giraffe window, and can overwrite the project's analytics and ` +
      `usages. You can still find a pack with fetchContentPacks and show the user what it ` +
      `contains with fetchPackContents; then ask them to add it from Giraffe's content library ` +
      `themselves. This is a policy refusal, not a missing or misspelled name: calling "${name}" ` +
      `through either SDK tool will be refused again.`
    );
  }
  if (DENIED.get(name) === PATCH_ONLY) {
    return (
      `"${name}" replaces a whole record, and a partial or stale copy silently resets the parts ` +
      `it leaves out — for app "1", the project's analytics and every usage. Use ` +
      `giraffe_sdk_patch with target "projectApp" instead: it reads the record fresh, changes ` +
      `only the paths you name, and confirms the write. This is a policy refusal, not a missing ` +
      `or misspelled name: calling "${name}" through either SDK tool will be refused again.`
    );
  }
  const reach = REACHES.get(name);
  if (reach !== undefined) {
    const instead = LAYER_WRITES.has(name)
      ? `To put a layer in this project, find it in a content pack instead: fetchContentPacks ` +
        `with contents ["layers"], then fetchPackContents on the pack, whose layers are ids of ` +
        `layers that already exist, then addLayerToProject with the id. That links the layer ` +
        `without creating a copy. If no pack has it, tell the user what layer is needed and ` +
        `they can add it in Giraffe.`
      : `If the user wants this change, tell them what to change and where, and they can make ` +
        `it in Giraffe.`;
    return (
      `"${name}" is a workspace-level procedure: it ${reach}. This surface acts only on the ` +
      `project this session is driving, so an agent cannot call it. This is a policy refusal, ` +
      `not a missing or misspelled name: retrying it will be refused too. ${instead}`
    );
  }
  if (name === "getUserClaimsJwt") {
    return (
      `"${name}" returns the user's signed identity token, and this surface does not expose ` +
      `credential-bearing procedures to an agent. This is a policy refusal, not a missing or ` +
      `misspelled name: retrying it will be refused too. The app proves the user's identity ` +
      `itself, so the token is not needed to continue.`
    );
  }
  return (
    `"${name}" returns shared third-party access tokens, and this surface does not expose ` +
    `credential-bearing procedures to an agent. This is a policy refusal, not a missing or ` +
    `misspelled name: retrying it, or reaching the same values by another procedure, will be ` +
    `refused too. The app applies the credentials it needs internally, so the raw values are ` +
    `not required to continue.`
  );
}

/**
 * Fail loudly on an RPC name the host does not register.
 *
 * This is the failure the app repos paid for in live sessions: an `rpc.invoke`
 * name is an undocumented string, `rpc` is untyped, and because RPC can
 * legitimately fail, call sites correctly wrap it in try/catch — so a phantom
 * name throws into a swallow and presents as NOTHING AT ALL. Two such names
 * lived in a shipped app for three weeks, documented as "fallbacks".
 */
export function assertHostRpcName(name: string): void {
  // ⚠️ A DENIED NAME PASSES THIS, deliberately. This function answers "is this
  // name real", and a denied name is real. The denial binds the generic
  // passthrough — see `DENIED_HOST_RPC_NAMES` — which is the only place an
  // AGENT chooses a procedure name. A declared tool naming one is a reviewed
  // decision by a human writing a descriptor, and `docs/tool-surface.md` § 4
  // plans exactly that.
  if (HOST_RPC_NAMES.has(name)) return;
  // #39: this used to say the call "would throw ProcedureNotFoundError at
  // runtime". For `updateProject` it would not — the walker had missed it. A
  // snapshot can say only what its own commit held.
  throw new Error(
    `"${name}" is not in the host RPC snapshot taken ${HOST_RPC_SNAPSHOT.takenAt} at ` +
      `giraffe-four@${HOST_RPC_SNAPSHOT.commit}. A name the host does not register throws into ` +
      `a try/catch and presents as nothing. If the host has since added it, run ` +
      `\`yarn rpc:refresh\` and review the diff.`,
  );
}
